import * as React from 'react';
import { RedactedText } from '@/components/ui/citation-chip';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import {
  errorMessage,
  rowsOf,
  useConfirmItems,
  useDeclineItems,
  useKbHeld,
  type KbProposal,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Actions, FormError, KindChip, Linkish, Quote } from '../kb-bits';
import { formatDate, skipReasonText } from '../kb-logic';
import { NarrowQuote } from './narrow-quote';
import { LibraryChoice, ReviewGroupShell } from './proposals';
import { DeclineChooser } from './review-bits';

/**
 * Proposals held for a curator's look (rule 13, E13-design §9 items 4, 9,
 * 10): a client name removed, a rating from Caliber v1 in the quote, a
 * proposal assessment from Caliber v1, or an answer not matched to a
 * library requirement. Each is decided in its own card: confirming one
 * records that the curator looked at it (`acknowledge`).
 */

const GROUPS: Array<{ reason: string; title: string; hint: string; tone: string }> = [
  {
    reason: 'name_removed',
    title: 'Client name removed',
    hint: 'Code replaced a protected name. Check that the quote still reads right and names no client.',
    tone: 'border-l-accent-amber-600',
  },
  {
    reason: 'v1_number',
    title: 'Rating from Caliber v1',
    hint: 'The quote holds a rating or score from Caliber v1, which the knowledge base never keeps. Narrow the quote to leave it out, or decline it.',
    tone: 'border-l-posture-at-risk',
  },
  {
    reason: 'v1_assessment',
    title: 'Proposal assessment from Caliber v1',
    hint: 'It assessed a proposal made to one client, so it can name people or that client. Confirm it only if it reads as a fact about the vendor.',
    tone: 'border-l-posture-unassessed',
  },
  {
    reason: 'no_library_item',
    title: 'Requirement not matched',
    hint: 'Pick the requirement-library item this answer is for, then confirm it.',
    tone: 'border-l-posture-unassessed',
  },
];

export function HeldGroups({ vendorId }: { vendorId: string }) {
  const held = useKbHeld(vendorId, true);
  const rows = rowsOf(held.data);
  return (
    <>
      {GROUPS.map((g) => {
        const mine = rows.filter((r) => (r.hold_reason ?? 'name_removed') === g.reason);
        if (mine.length === 0) return null;
        return (
          <ReviewGroupShell
            key={g.reason}
            id={`kb-review-${g.reason}`}
            title={g.title}
            count={mine.length}
            hint={g.hint}
          >
            {mine.map((p) => (
              <HeldCard key={p.id} proposal={p} tone={g.tone} />
            ))}
          </ReviewGroupShell>
        );
      })}
      {held.hasNextPage ? (
        <Linkish onClick={() => void held.fetchNextPage()} disabled={held.isFetchingNextPage}>
          Show more held items
        </Linkish>
      ) : null}
    </>
  );
}

function HeldCard({ proposal: p, tone }: { proposal: KbProposal; tone: string }) {
  const { toast } = useToast();
  const confirm = useConfirmItems();
  const decline = useDeclineItems();
  const [open, setOpen] = React.useState<'narrow' | 'decline' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const reason = p.hold_reason ?? 'name_removed';
  const source =
    p.source?.kind === 'avero_kb'
      ? 'the Avero Advisors Vendor Knowledge Base'
      : (p.source?.title ?? p.source?.file_name ?? 'its source');

  const confirmIt = () => {
    setError(null);
    confirm.mutate(
      { ids: [p.id], acknowledge: [p.id] },
      {
        onSuccess: (reply) => {
          const done = reply.confirmed[0];
          if (done) {
            toast({ message: `${done.number} confirmed. It counts now.` });
            return;
          }
          const cite = reply.became_citation[0];
          if (cite) {
            toast({
              message: `It means the same as ${cite.item_number ?? 'an item'}, so it was offered as a further citation of it.`,
            });
            return;
          }
          const skip = reply.skipped[0];
          if (skip) setError(`Not confirmed: ${skipReasonText(skip.reason)}.`);
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  return (
    <div
      className={cn('rounded-card border border-l-4 border-ink-100 bg-paper-0 px-4 py-3.5', tone)}
    >
      <div className="flex items-center gap-2.5">
        <span className="min-w-0 flex-1 font-semibold text-ink-900">
          {p.statement ? (
            <RedactedText text={p.statement} highlight={reason === 'name_removed'} />
          ) : (
            <span className="font-normal text-ink-500">No statement yet</span>
          )}
        </span>
        <KindChip kind={p.kind} />
      </div>
      {open === 'narrow' ? (
        <NarrowQuote
          proposal={p}
          onDone={() => {
            setOpen(null);
            toast({ message: 'Quote narrowed. It was checked against its source again.' });
          }}
          onCancel={() => setOpen(null)}
        />
      ) : (
        <Quote text={p.excerpt_text} highlightRemoved={reason === 'name_removed'} />
      )}
      <div className="mt-1.5 text-meta text-ink-500">
        From {source}, {formatDate(p.created_at)}.
        {reason === 'name_removed' ? ' The original wording is kept only for checking quotes.' : ''}
      </div>
      {reason === 'no_library_item' && p.library_suggestions?.length ? (
        <LibraryChoice proposal={p} />
      ) : null}
      {error ? <FormError>{error}</FormError> : null}
      {open === 'decline' ? (
        <DeclineChooser
          pending={decline.isPending}
          onCancel={() => setOpen(null)}
          onDecline={(code, text) =>
            decline.mutate(
              { ids: [p.id], reason_code: code, ...(text ? { reason_text: text } : {}) },
              {
                onSuccess: () => toast({ message: 'Declined. It stays on record.' }),
                onError: (err) => setError(errorMessage(err)),
              },
            )
          }
        />
      ) : open === null ? (
        <Actions>
          <WorkspaceButton variant="primary" disabled={confirm.isPending} onClick={confirmIt}>
            {reason === 'name_removed' || reason === 'v1_assessment' ? 'Looks right' : 'Confirm'}
          </WorkspaceButton>
          <WorkspaceButton onClick={() => setOpen('narrow')}>Narrow the quote</WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={() => setOpen('decline')}>
            Decline
          </WorkspaceButton>
        </Actions>
      ) : null}
    </div>
  );
}
